import {Modal, Form, Input, Button, Space, Alert} from 'antd';
import {Form as AntForm} from 'antd';
import {UserSelectField} from 'components/userSelectField/UserSelectField';

/**
 * Link an anonymous ticket to a verified customer account.
 *
 * Anonymous tickets carry `origin = ANONYMOUS` and (until linked) `userId = null`.
 * The picker uses UserSelectField with `admin=false` to list customer accounts.
 * The select returns `firstName;lastName;id;email` — id is at index 2.
 *
 * The `note` is recorded with the link event (audit trail) so the agent can
 * justify *why* this is the right account (e.g. verified DOB + last txn).
 * Origin stays ANONYMOUS after linking — only `userId`, `linkedAt`, and
 * `linkedByAgentId` change. The agent id comes from the auth context, not
 * the payload.
 */
export const LinkAnonymousTicketModal = ({
  isOpen,
  onClose,
  onLink,
  loading,
  contactEmail,
}) => {
  const [form] = Form.useForm();
  const userSelection = AntForm.useWatch('user', form);

  const handleFinish = values => {
    const parts = String(values.user || '').split(';');
    const userId = (parts[2] || '').trim();
    const userName = `${(parts[0] || '').trim()} ${(
      parts[1] || ''
    ).trim()}`.trim();
    if (!userId) {
      form.setFields([
        {name: 'user', errors: ['Please pick a customer from the list.']},
      ]);
      return;
    }
    onLink({userId, userName, note: (values.note || '').trim() || null}, () => {
      form.resetFields();
      onClose();
    });
  };

  return (
    <Modal
      title="Link Anonymous Ticket to Account"
      open={isOpen}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      footer={null}
      width={520}
      destroyOnClose>
      <Alert
        type="warning"
        showIcon
        style={{marginBottom: 16}}
        message="Verify the customer's identity out-of-band first (e.g. DOB + last transaction). Linking attaches this ticket to their account; the ticket id stays the customer's only credential for the conversation."
      />
      {contactEmail ? (
        <Alert
          type="info"
          showIcon
          style={{marginBottom: 16}}
          message={
            <span>
              Contact email on the ticket: <strong>{contactEmail}</strong>{' '}
              (unverified — typed by the customer at ticket creation)
            </span>
          }
        />
      ) : null}
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="user"
          label="Customer account"
          rules={[{required: true, message: 'Please select a customer'}]}>
          <UserSelectField
            users={userSelection}
            mode="single"
            setUsers={value => form.setFieldValue('user', value)}
            placeholder="Search customers by name or email"
          />
        </Form.Item>
        <Form.Item
          name="note"
          label="Verification note (recommended)"
          extra="What you used to verify identity — kept on the link record for audit.">
          <Input.TextArea
            rows={3}
            placeholder="e.g. Verified DOB + last transaction amount over the phone."
            maxLength={500}
            showCount
          />
        </Form.Item>
        <Form.Item style={{marginBottom: 0}}>
          <Space style={{width: '100%', justifyContent: 'flex-end'}}>
            <Button
              onClick={() => {
                onClose();
                form.resetFields();
              }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Link to Account
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
